import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Alert,
  Box,
  Button,
  ClickAwayListener,
  FormControl,
  Grid,
  Paper,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import * as React from "react";
import { initialAppState } from "../../common/reducers/appState";
import { fetchData } from "../../common/services/ApiClient";
import {
  fileNameFormat,
  formatRepDate,
  getCookiebyName,
  sortObjValue,
} from "../../common/services/Utilities";
import { ApiBaseUrl } from "../../config";
import FinancialCustomReportStructure from "../../containers/FinancialCustomReportStructure";
import FinancialReportStructure from "../../containers/FinancialReportStructure";
import ReportingPeriod from "../../containers/ReportingPeriod";
import {
  Application,
  KeyBooleanValue,
  KeyValue,
  Query,
} from "../../interfaces";
import ApiDataDownload, {
  DownloadHeaders,
  apiDownloadProps,
} from "../ApiDataDownload";
import LoadingIndicator from "../LoadingIndicator";
import { standardComparisonOptions } from "../PeerGroup/PeerGroupUtilities";
import LoadReportModal from "../../containers/LoadReportModal";

interface Props {
  appState: Query;
  uuid: string;
  data: KeyValue[];
  fromLevelOne: boolean;
  updateUi: (
    filter: string,
    value: string | string[] | KeyValue[] | boolean,
  ) => void;
  updateFilters: (filter: string, value: string[]) => void;
  resetState: (state: Query) => void;
  currentApp: Application;
  formHeader?: React.ReactElement;
  dataLoading?: boolean;
  customVariant?: boolean;
  returnPath?: string;
  peerGroupsInfo?: KeyValue[] | null;
}

interface FRDetails {
  reportPeriod: string[];
  reportType: string;
  incomeBasis: string[];
  unitType: string[];
}

const incomeBasisChoice = [
  { value: "YTD", label: "Year-To-Date" },
  { value: "Qtly", label: "Quarterly" },
];

const unitTypeChoice = [
  { value: "$", label: "US Dollars ($)" },
  { value: "%", label: "Percentages (%)" },
];

const nonEditable = (
  <Typography
    align="center"
    color="primary.main"
    style={{ fontStyle: "italic", marginTop: "1rem" }}
    id="non-editable"
  >
    Non-editable
  </Typography>
);

export const pushDownloadHeaderArray = (
  fields: KeyValue[],
  downloadHeaderArray: DownloadHeaders[],
) => {
  if (!Array.isArray(fields)) return;
  fields.forEach((fieldVal: KeyValue) => {
    const ytdVal = fieldVal.variable_ytd;
    const ytdValr = fieldVal.variable_ytdr;
    const qtlyVal = fieldVal.variable_qtly;
    const qtlyValr = fieldVal.variable_qtlyr;

    if (ytdVal) {
      downloadHeaderArray.push({ label: ytdVal, key: ytdVal });
    }
    if (ytdValr && ytdVal !== ytdValr) {
      downloadHeaderArray.push({ label: ytdValr, key: ytdValr });
    }
    if (qtlyVal && ytdVal !== qtlyVal) {
      downloadHeaderArray.push({ label: qtlyVal, key: qtlyVal });
    }
    if (qtlyValr && ytdValr !== qtlyValr) {
      downloadHeaderArray.push({ label: qtlyValr, key: qtlyValr });
    }
    if (fieldVal.sub_fields) {
      pushDownloadHeaderArray(fieldVal.sub_fields, downloadHeaderArray);
    }
  });
};

export default function FinancialReportDetailsSearch({
  appState,
  uuid,
  data,
  fromLevelOne,
  updateUi,
  updateFilters,
  resetState,
  currentApp,
  formHeader,
  dataLoading,
  customVariant,
  returnPath,
  peerGroupsInfo,
}: Props) {
  const standardReportTypes = [
    "summary-table",
    "income-and-expense",
    "performance-and-condition-ratios",
    "assets-liabilities-and-capital",
  ];
  const defaultReportValues = [
    { title: "Summary Table" },
    { title: "Income and Expense" },
    { title: "Performance and Condition Ratios" },
    { title: "Assets, Liabilities, and Capital" },
  ];
  if (currentApp === "financialReporting") {
    defaultReportValues.push({ title: "Demographics" });
  }
  const [reportTypes, setReportTypes] =
    React.useState<KeyValue[]>(defaultReportValues);
  const [fetchReports, setFetchReports] = React.useState(true);
  const [savedReports, setSavedReports] = React.useState<KeyValue[]>([]);
  const [displayReportLoading, setDisplayReportLoading] = React.useState(false);
  const [FRDetails, setFRDetails] = React.useState<FRDetails>({
    reportPeriod: appState.filters.reportPeriod,
    reportType: appState.ui.reportType,
    incomeBasis: appState.ui.incomeBasis,
    unitType: appState.ui.unitType,
  });

  const prevFRDetails: FRDetails = {
    reportPeriod: appState.filters.reportPeriod,
    reportType: appState.ui.reportType,
    incomeBasis: appState.ui.incomeBasis,
    unitType: appState.ui.unitType,
  };

  const [displayDetails, setDisplayDetails] = React.useState<boolean[]>(
    Array.isArray(appState.filters.reportPeriod)
      ? appState.filters.reportPeriod.map(() => false)
      : [],
  );

  const [fetchReportData, setFetchReportData] = React.useState<boolean>(false);
  const [hideSearchButton, setHideSearchButton] =
    React.useState<boolean>(false);
  const [updateReportButton, setUpdateReportButton] =
    React.useState<boolean>(false);
  const [customReportButton, setCustomReportButton] =
    React.useState<boolean>(false);
  const [newReportDisplay, setNewReportDisplay] =
    React.useState<boolean>(false);

  const [hasPendingUpdate, setHasPendingUpdate] =
    React.useState<boolean>(false);

  const [pendingFRDetails, setPendingFRDetails] =
    React.useState<FRDetails | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const [currentIndex, setCurrentIndex] = React.useState<number>(0);

  const [reportMetaData, setReportMetaData] = React.useState<KeyValue>({
    income_basis: ["YTD"],
    unit_type: ["$"],
  });

  const [previousReportMetaData, setPreviousReportMetaData] =
    React.useState(reportMetaData);

  const [newComparison, setNewComparison] = React.useState({
    reportPeriod: [""],
    incomeBasis: [""],
    unitType: [""],
  });

  const [noDataCheck, setNoDataCheck] = React.useState(true);

  const [initialLoad, setInitialLoad] = React.useState(true);
  const [institutionsData, setInstitutionsData] = React.useState<KeyValue>();
  const [fetchInstSTMULT, setFetchInstSTMULT] = React.useState(false);
  const [hasModalChange, setHasModalChange] = React.useState<boolean>(false);

  const currentIncomeBasis = newReportDisplay
    ? newComparison.incomeBasis[currentIndex]
    : FRDetails.incomeBasis?.[currentIndex];

  const currentUnitType = newReportDisplay
    ? newComparison.unitType[currentIndex]
    : FRDetails.unitType?.[currentIndex];

  const reportPeriod = newReportDisplay
    ? newComparison.reportPeriod
    : FRDetails.reportPeriod
      ? FRDetails.reportPeriod
      : appState.filters.reportPeriod;

  const reportDate = reportPeriod?.map((report: string) => {
    return {
      month: report?.substring(4, 6),
      day: report?.substring(6, 8),
      year: report?.substring(0, 4),
    };
  });

  const customReportHeaderDisplay =
    appState.ui.reportType === "custom" || customVariant;

  const loadCustomReport =
    appState.ui.loadModal || appState.ui.reportType === "customResults";

  const cert = data[0]?.CERT || window.location.pathname.split("/").pop() || "";

  const customReportResultsDisplay = appState.ui.reportType === "customResults";
  const defaultReportTypes = [
    "summary-table",
    "income-and-expense",
    "performance-and-condition-ratios",
    "assets-liabilities-and-capital",
    "custom",
    "customResults",
    "demographics",
  ].includes(FRDetails.reportType);

  const isPeerGroup = currentApp === "peerGroup";
  const pre1992 = appState.filters.reportPeriod?.[0]
    ? Number(appState.filters.reportPeriod?.[0]) < 19920331
    : Number(appState.ui.latestReportPeriod) < 19920331;

  React.useEffect(() => {
    if (
      FRDetails.reportType === "performance-and-condition-ratios" &&
      FRDetails.unitType.includes("$")
    ) {
      const newUnitTypes = FRDetails.unitType.map(() => "%");
      const pending: FRDetails = {
        reportType: FRDetails.reportType,
        reportPeriod: [...FRDetails.reportPeriod],
        incomeBasis: [...FRDetails.incomeBasis],
        unitType: [...newUnitTypes],
      };
      setFRDetails(pending);
    }
  }, [FRDetails]);
  React.useEffect(() => {
    if (
      FRDetails.reportType === "assets-liabilities-and-capital" &&
      appState.ui.incomeBasis.includes("Qtly")
    ) {
      const newIncomeBasis = appState.ui.incomeBasis.map(() => "YTD");
      const pending: FRDetails = {
        reportType: FRDetails.reportType,
        reportPeriod: [...appState.filters.reportPeriod],
        incomeBasis: [...newIncomeBasis],
        unitType: [...appState.ui.unitType],
      };
      setFRDetails(pending);
    }
  }, [
    FRDetails.reportType,
    appState.filters.reportPeriod,
    appState.ui.incomeBasis,
    appState.ui.unitType,
  ]);

  React.useEffect(() => {
    if (appState.ui.resetReportState) {
      setUpdateReportButton(false);
      updateUi("resetReportState", false);
      updateUi("reportType", "");
      updateUi("reportStructure", "");
      setFRDetails((prev) => ({
        ...prev,
        reportType: "",
      }));
    }
  }, [appState.ui.resetReportState, setFRDetails, FRDetails, updateUi]);

  React.useEffect(() => {
    let isMounted = true;

    if (
      isMounted &&
      appState.ui.reportType === "demographics" &&
      !institutionsData &&
      cert
    ) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/institutions?filters=CERT:${cert}&limit=1`,
        )
          .then((response) => {
            setInstitutionsData(response.data[0]);
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
            setFetchInstSTMULT(true);
          });
      })();
    } else if (
      appState.ui.reportType !== "demographics" &&
      institutionsData !== undefined
    ) {
      setInstitutionsData(undefined);
    }
  }, [
    FRDetails.reportType,
    appState.ui.reportType,
    cert,
    fetchReportData,
    fetchReports,
    institutionsData,
  ]);
  React.useEffect(() => {
    let isMounted = true;

    if (fetchInstSTMULT && institutionsData && isMounted) {
      let formattedRisDate = institutionsData.RISDATE.split("/");
      formattedRisDate = `${formattedRisDate[2]}${formattedRisDate[0]}${formattedRisDate[1]}`;
      setFetchInstSTMULT(false);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/events?agg_by=STNAME&filters=!OFF_SERVTYPE:26 ORG_END_NUM_DTE:>=${formattedRisDate} AND ORG_EFF_NUM_DTE:<=${formattedRisDate} AND CERT:${cert} AND BANK_INSURED:Y AND !CLASS:CU&get_unique=STNAME`,
        )
          .then((response) => {
            if (isMounted) {
              const multiState = response.totals.count > 1 ? "1" : "0";
              setInstitutionsData({ ...institutionsData, STMULT: multiState });
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [appState.ui.reportType, cert, fetchInstSTMULT, institutionsData]);

  React.useEffect(() => {
    let isMounted = true;
    const user = uuid && uuid !== "LOADING" ? uuid : getCookiebyName("uuid");
    if (isMounted && user && fetchReports) {
      setFetchReports(false);

      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/report_types?filters=is_custom:true&user=${user}&limit=10000&offset=0`,
        )
          .then((response) => {
            setSavedReports(response.data);
            let customReports: KeyValue[] = response.data?.map(
              (report: KeyValue) => {
                const demographicArray = [
                  "ACTIVE",
                  "NAMEHCR",
                  "CERT",
                  "RSSDID",
                  "NAME",
                  "CITY",
                  "STALP",
                  "ZIP",
                  "OFFDOM",
                  "OFFFOR",
                  "STMULT",
                  "OFFOA",
                  "SPECGRPDESC",
                  "SUBCHAPS",
                  "STCNTY",
                  "CBSA",
                  "ESTYMD",
                  "INSDATE",
                  "PROCDATE",
                  "EFFDATE",
                  "MUTUAL",
                  "PARCERT",
                  "CB",
                  "TRUST",
                  "BKCLASS",
                  "REGAGNT",
                  "CFPBFLAG",
                  "INSAGNT1",
                  "QBPRCOMLDESC",
                  "FDICDBSDESC",
                  "FDICSUPVDESC",
                  "FLDOFF",
                  "FEDDESC",
                  "OCCDISTDESC",
                  "WEBADDR",
                  "TE01N529",
                  "TE02N529",
                  "TE03N529",
                  "TE04N529",
                  "TE05N529",
                  "TE06N529",
                ];
                const containstDemographics = report.structure?.[0].fields.some(
                  (val: KeyValue) =>
                    demographicArray.includes(val.variable_ytd),
                );
                return {
                  title: report.title,
                  id: report.ID,
                  containsDemographics: containstDemographics,
                };
              },
            );
            if (currentApp === "peerGroup") {
              customReports = customReports.filter(
                (report) => !report.containsDemographics,
              );
            }
            setReportTypes([...reportTypes, ...customReports]);
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            const timer = setTimeout(() => {
              setDisplayReportLoading(false);
            }, 200);
            return () => {
              isMounted = false;
              clearTimeout(timer);
            };
          });
      })();
    }
  }, [
    currentApp,
    displayReportLoading,
    fetchReports,
    reportTypes,
    savedReports,
    updateUi,
    uuid,
  ]);
  React.useEffect(() => {
    if (Number(cert) && !appState.ui.minRepDates && noDataCheck) {
      setNoDataCheck(false);
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/financials?limit=1&offset=0&filters=CERT:${cert}&fields=ESTYMD`,
        )
          .then((response) => {
            if (response.data.length === 0) {
              updateUi("noFRdata", true);
            } else if (
              isMounted &&
              response.data[0].ESTYMD &&
              response.data[0].ESTYMD
            ) {
              const establishedYear =
                response.data[0].ESTYMD.toString().substring(0, 4);
              const estaglishedMD =
                response.data[0].ESTYMD.toString().substring(4, 8);

              const earliestDataQtr = ["1231", "0930", "0630", "0331"]
                .map((quarter: string) => {
                  return Number(estaglishedMD) <= Number(quarter)
                    ? quarter
                    : "";
                })
                .filter((val) => val !== "")
                .slice(-1)[0];

              updateUi("minRepDates", [`${establishedYear}${earliestDataQtr}`]);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [
    appState.ui.maxRepDates,
    appState.ui.minRepDates,
    cert,
    noDataCheck,
    updateUi,
  ]);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (
      urlParams.get("limitInactiveDate") === "true" &&
      !appState.ui.limitInactiveDate
    ) {
      updateUi("limitInactiveDate", true);
    }
  }, [appState.ui.limitInactiveDate, updateUi]);

  React.useEffect(() => {
    if (!customReportHeaderDisplay && !initialLoad) {
      setInitialLoad(true);
    } else if (customReportHeaderDisplay && initialLoad) {
      setInitialLoad(false);
      updateUi("incomeBasis", ["YTD"]);
      updateUi("unitType", ["$"]);
      updateFilters("reportPeriod", [appState.filters.reportPeriod?.[0]]);
      setFRDetails({
        ...FRDetails,
        incomeBasis: ["YTD"],
        unitType: ["$"],
        reportPeriod: [appState.filters.reportPeriod?.[0]],
      });
    }
  }, [
    FRDetails,
    appState.filters.reportPeriod,
    customReportHeaderDisplay,
    customReportResultsDisplay,
    initialLoad,
    updateFilters,
    updateUi,
  ]);

  React.useEffect(() => {
    if (fromLevelOne) {
      resetState({
        ...initialAppState.financialReporting,
        filters: {
          ...initialAppState.financialReporting.filters,
          reportPeriod: appState.filters.reportPeriod,
        },
      });
      setFRDetails({
        reportPeriod: appState.filters.reportPeriod,
        reportType: initialAppState.financialReporting.ui.reportType,
        incomeBasis: initialAppState.financialReporting.ui.incomeBasis,
        unitType: initialAppState.financialReporting.ui.unitType,
      });
    }
  }, [appState.filters.reportPeriod, fromLevelOne, resetState]);
  React.useEffect(() => {
    if (!appState.ui.reportType) {
      setHideSearchButton(false);
    } else if (
      prevFRDetails.reportType === FRDetails.reportType ||
      !FRDetails.reportType
    ) {
      setHideSearchButton(true);
      setUpdateReportButton(
        fromLevelOne || appState.ui?.resetReportState ? false : true,
      );
      setReportMetaData(getReportMetaData(appState.ui.reportType.toString()));
    } else if (
      FRDetails.reportType === "customResults" ||
      FRDetails.reportType === "loadModal"
    ) {
      setHideSearchButton(true);
    } else {
      setHideSearchButton(false);
    }
  }, [
    FRDetails.reportType,
    appState.ui.reportType,
    appState.ui?.resetReportState,
    fromLevelOne,
    prevFRDetails.reportType,
  ]);
  React.useEffect(() => {
    if (customVariant && FRDetails.reportType !== "custom") {
      setFRDetails({ ...FRDetails, reportType: "custom" });
      setFetchReportData(true);
    }
  }, [FRDetails, customVariant]);
  React.useEffect(() => {
    if (
      fetchReportData ||
      (appState.ui.reportType && !appState.ui.reportStructure)
    ) {
      setFetchReportData(false);
      let isMounted = true;
      const filter =
        FRDetails.reportType === "custom"
          ? "?filters=is_custom:false"
          : defaultReportTypes
            ? `?filters=ID:"${FRDetails.reportType}"`
            : `?filters=_id:${FRDetails.reportType}&user=${getCookiebyName(
                "uuid",
              )}`;
      if (
        FRDetails.reportType &&
        FRDetails.reportType !== "customResults" &&
        FRDetails.reportType !== "loadModal" &&
        (FRDetails.reportType !== appState.ui.reportType || initialLoad)
      ) {
        (async () => {
          await fetchData(`${ApiBaseUrl()}/report_types${filter}`)
            .then((response) => {
              if (isMounted) {
                const structure = response?.data?.map((data: KeyValue) => {
                  return data?.structure?.map((val: KeyValue) =>
                    val.structureID ? val : { ...val, structureID: data.ID },
                  );
                });
                updateUi("reportType", FRDetails.reportType);
                updateUi("reportStructure", [].concat.apply([], structure));
              }
            })
            .catch((error) => {
              console.error(error);
            })
            .finally(() => {
              isMounted = false;
            });
        })();
      }
    }
  }, [
    FRDetails.reportType,
    appState.ui.reportStructure,
    appState.ui.reportType,
    defaultReportTypes,
    fetchReportData,
    initialLoad,
    updateUi,
  ]);
  React.useEffect(() => {
    if (
      customReportResultsDisplay &&
      FRDetails.reportType === "custom" &&
      !fetchReportData
    ) {
      updateUi("reportType", "customResults");
      setFRDetails({
        ...FRDetails,
        reportType: "customResults",
      });
    } else if (
      appState.ui.reportType !== "customResults" &&
      FRDetails.reportType !== "custom" &&
      appState.ui.customVariables &&
      appState.ui.customVariables.length > 0 &&
      !fetchReportData
    ) {
      updateUi("customVariables", []);
    }
  }, [
    FRDetails,
    appState.ui.customVariables,
    appState.ui.reportType,
    customReportResultsDisplay,
    fetchReportData,
    updateUi,
  ]);

  React.useEffect(() => {
    if (
      JSON.stringify(previousReportMetaData) !== JSON.stringify(reportMetaData)
    ) {
      setPreviousReportMetaData(reportMetaData);
      setFRDetails({
        ...FRDetails,
        incomeBasis: appState.ui.incomeBasis.map((incomeBasis: string) => {
          return reportMetaData.income_basis.includes(incomeBasis)
            ? incomeBasis
            : reportMetaData.income_basis[0];
        }),
        unitType: appState.ui.unitType.map((unitType: string) => {
          return reportMetaData.unit_type.includes(unitType)
            ? unitType
            : reportMetaData.unit_type[0];
        }),
      });
    }
  }, [
    FRDetails,
    appState.ui.incomeBasis,
    appState.ui.unitType,
    previousReportMetaData,
    reportMetaData,
  ]);

  function generateReport() {
    if (FRDetails.reportType) {
      setHideSearchButton(true);
    } else {
      setHideSearchButton(false);
    }
    setFetchReportData(true);

    if (newReportDisplay) {
      setFRDetails({
        ...FRDetails,
        reportPeriod: newComparison.reportPeriod,
        incomeBasis: newComparison.incomeBasis,
      });
      setDisplayDetails(displayDetails.map(() => false));
      setNewReportDisplay(false);
      updateUi("incomeBasis", newComparison.incomeBasis);
      updateUi("unitType", newComparison.unitType);
      updateFilters("reportPeriod", newComparison.reportPeriod);
    } else if (pendingFRDetails) {
      setFRDetails({
        ...FRDetails,
        reportPeriod: pendingFRDetails.reportPeriod,
        incomeBasis: pendingFRDetails.incomeBasis,
      });
      updateUi("incomeBasis", pendingFRDetails.incomeBasis);
      updateUi("unitType", pendingFRDetails.unitType);
      if (
        JSON.stringify(pendingFRDetails.reportPeriod) !==
        JSON.stringify(appState.filters.reportPeriod)
      ) {
        updateFilters("reportPeriod", pendingFRDetails.reportPeriod);
      }
    } else {
      updateUi("incomeBasis", FRDetails.incomeBasis);
      updateUi("unitType", FRDetails.unitType);
    }

    setHasPendingUpdate(false);
    setPendingFRDetails(null);
  }

  // returns an array of indicies of columns with pending changes ex. [1, 3]
  function findColumnsWithChangesPending(current: any, pending: any) {
    const frProperties = ["reportPeriod", "incomeBasis", "unitType"];
    const length = current[frProperties[0]].length;
    const differingIndices = [];

    for (let i = 0; i < length; i++) {
      const differs = frProperties.some(
        (key) => current[key][i] !== pending[key][i],
      );
      if (differs) differingIndices.push(i);
    }

    return differingIndices;
  }

  const columnsWithPendingChanges =
    pendingFRDetails &&
    findColumnsWithChangesPending(
      {
        ...FRDetails,
        incomeBasis:
          FRDetails.reportType === "assets-liabilities-and-capital"
            ? FRDetails.incomeBasis
            : appState.ui.incomeBasis,
        reportPeriod: appState.filters.reportPeriod,
        unitType:
          FRDetails.reportType === "performance-and-condition-ratios"
            ? FRDetails.unitType
            : appState.ui.unitType,
      },
      pendingFRDetails,
    );

  const handleOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number,
    newReport?: boolean,
  ) => {
    setCurrentIndex(index);
    setHasModalChange(false);
    setDisplayDetails(
      index >= displayDetails.length
        ? [...displayDetails, true]
        : displayDetails.map(
            (_display: boolean, displayIndex: number) => index === displayIndex,
          ),
    );

    const hasPendingChange = columnsWithPendingChanges?.includes(index);

    setAnchorEl(event.currentTarget);
    if (newReport) {
      setNewComparison({
        reportPeriod: [
          ...appState.filters.reportPeriod,
          appState.ui.latestReportPeriod,
        ],
        incomeBasis: [
          ...appState.ui.incomeBasis,
          reportMetaData.income_basis[0],
        ],
        unitType: [...appState.ui.unitType, reportMetaData.unit_type[0]],
      });

      if (unitChecked[`unitType_${index}`]) {
        delete unitChecked[`unitType_${index}`];
      }
      setNewReportDisplay(true);
    } else {
      setNewReportDisplay(false);
    }

    if (hasPendingChange) return;

    if (
      !unitChecked[`unitType_${index}`] &&
      FRDetails.unitType[index] === "%"
    ) {
      setUnitChecked({ ...unitChecked, [`unitType_${index}`]: true });
    } else {
      setUnitChecked({ ...unitChecked, [`unitType_${index}`]: false });
    }
    if (
      JSON.stringify(FRDetails.unitType) !==
        JSON.stringify(appState.ui.unitType) &&
      FRDetails.reportType === appState.ui.reportType
    ) {
      const updatedUnitChecked = appState.ui.unitType
        .map((unitType: string, int: number) => {
          const unitCheckedObj: any = {};
          return {
            ...unitCheckedObj,
            key: `unitType_${int}`,
            value: unitType === "%",
          };
        })
        .reduce(
          (obj: any, item: KeyValue) =>
            Object.assign(obj, { [item.key]: item.value }),
          {},
        );

      setUnitChecked(updatedUnitChecked);
      setFRDetails({ ...FRDetails, unitType: appState.ui.unitType });
    }
    if (
      JSON.stringify(FRDetails.incomeBasis) !==
        JSON.stringify(appState.ui.incomeBasis) &&
      FRDetails.reportType === appState.ui.reportType
    ) {
      setFRDetails({ ...FRDetails, incomeBasis: appState.ui.incomeBasis });
    }
    if (
      JSON.stringify(appState.filters.reportPeriod) !==
      JSON.stringify(FRDetails.reportPeriod)
    ) {
      setFRDetails({
        ...FRDetails,
        reportPeriod: appState.filters.reportPeriod,
      });
    }
  };

  const handleModalClose = (generateReport?: boolean) => {
    if (
      !generateReport &&
      appState.ui?.reportStructure?.length &&
      Object.keys(appState.ui.reportStructure).length > 0 &&
      appState.ui?.loadModal
    ) {
      //This loads the previous report if a user cancels the modal
      updateUi("loadModal", false);
      if (appState.ui?.prevReportType) {
        updateUi("reportType", appState.ui?.prevReportType);
        setFRDetails({
          ...FRDetails,
          reportType: appState.ui?.prevReportType,
        });
      }
    } else {
      //This resets the search form if there was no previously displayed report if a user cancels the modal
      !generateReport && updateUi("resetReportState", true);
      updateUi("loadModal", false);
    }
  };
  const handleClose = () => {
    setDisplayDetails(displayDetails.map(() => false));

    const base: FRDetails = pendingFRDetails ?? {
      reportType: FRDetails.reportType,
      reportPeriod: [...FRDetails.reportPeriod],
      incomeBasis: [...FRDetails.incomeBasis],
      unitType: [...FRDetails.unitType],
    };

    const staged: FRDetails = {
      ...base,
      reportPeriod: base.reportPeriod.map((val, i) =>
        i === currentIndex ? FRDetails.reportPeriod[i] : val,
      ),
      incomeBasis: base.incomeBasis.map((val, i) =>
        i === currentIndex ? FRDetails.incomeBasis[i] : val,
      ),
      unitType: base.unitType.map((val, i) =>
        i === currentIndex ? FRDetails.unitType[i] : val,
      ),
    };

    setPendingFRDetails(staged);
    setHasPendingUpdate(true);
  };

  const removeAndClose = () => {
    setDisplayDetails(displayDetails.map(() => false));

    const newReports = appState.filters.reportPeriod.filter(
      (_: any, i: number) => i !== currentIndex,
    );
    const newIncomeBasis = appState.ui.incomeBasis.filter(
      (_: any, i: number) => i !== currentIndex,
    );
    const newUnitType = appState.ui.unitType.filter(
      (_: any, i: number) => i !== currentIndex,
    );

    setFRDetails({
      ...FRDetails,
      reportPeriod: newReports,
      incomeBasis: newIncomeBasis,
      unitType: newUnitType,
    });
    updateUi("incomeBasis", newIncomeBasis);
    updateUi("unitType", newUnitType);
    updateFilters("reportPeriod", newReports);
    setFetchReportData(true);
  };

  const getReportMetaData = (report: string) => {
    switch (report) {
      case "summary-table":
      case "income-and-expense":
        return {
          income_basis: ["YTD", "Qtly"],
          unit_type: ["$", "%"],
        };
      case "performance-and-condition-ratios":
        return {
          income_basis: ["YTD", "Qtly"],
          unit_type: ["%"],
        };
      case "assets-liabilities-and-capital":
        return {
          income_basis: ["YTD"],
          unit_type: ["$", "%"],
        };
      case "customResults":
      case "demographics":
        return {
          income_basis: [""],
          unit_type: [""],
        };
      default:
        return {
          income_basis: ["YTD"],
          unit_type: ["$"],
        };
    }
  };

  const handleReportChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value && e.target.value === "custom") {
      setCustomReportButton(true);
    } else {
      setCustomReportButton(false);
    }
    if (e.target.value && e.target.value === "loadModal") {
      appState.ui?.reportType &&
        updateUi("prevReportType", appState.ui.reportType);
      updateUi("loadModal", true);
    }
    setFRDetails({ ...FRDetails, reportType: e.target.value });
    setReportMetaData(getReportMetaData(e.target.value));
  };

  const handleIncomeBasisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasModalChange(true);
    // on change, it maps through all of the current reports, and changes the current one based on the index passed down
    const updatedIncomeBasis = newReportDisplay
      ? newComparison.incomeBasis.map((income: string, index: number) =>
          index === currentIndex ? e.target.value : income,
        )
      : FRDetails.incomeBasis.map((income: string, index: number) =>
          index === currentIndex ? e.target.value : income,
        );
    newReportDisplay
      ? setNewComparison({ ...newComparison, incomeBasis: updatedIncomeBasis })
      : setFRDetails({ ...FRDetails, incomeBasis: updatedIncomeBasis });
    if (customReportHeaderDisplay) {
      updateUi("incomeBasis", [e.target.value]);
      setFRDetails({ ...FRDetails, incomeBasis: [e.target.value] });
    }
  };

  const [unitChecked, setUnitChecked] = React.useState<KeyBooleanValue>({
    unitType_0: false,
  });
  const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasModalChange(true);
    // on change, it maps through all of the current reports, and changes the current one based on the index passed down
    setUnitChecked({ ...unitChecked, [e.target.name]: e.target.checked });

    const updatedUnitType = newReportDisplay
      ? newComparison.unitType.map((unit: string, index: number) =>
          index === currentIndex ? (e.target.checked ? "%" : "$") : unit,
        )
      : FRDetails.unitType.map((unit: string, index: number) =>
          index === currentIndex ? (e.target.checked ? "%" : "$") : unit,
        );

    newReportDisplay
      ? setNewComparison({ ...newComparison, unitType: updatedUnitType })
      : setFRDetails({ ...FRDetails, unitType: updatedUnitType });
    if (customReportHeaderDisplay) {
      updateUi("unitType", [e.target.value]);
      setFRDetails({ ...FRDetails, unitType: [e.target.value] });
    }
  };

  const downloadHeaderArray: DownloadHeaders[] = [];

  if (
    appState.ui.reportStructure &&
    appState.ui.reportStructure.length &&
    appState.ui.reportStructure[0]?.fields
  ) {
    pushDownloadHeaderArray(
      appState.ui.reportStructure[0].fields,
      downloadHeaderArray,
    );
  }

  const headerArrayValues: KeyValue[] = [];

  const mergedDownloadHeaders = [
    ...downloadHeaderArray
      .map((header: KeyValue) => {
        if (header.key && header.key.includes(",")) {
          return header.key.split(",").map((key: string) => {
            return headerArrayValues.push({ label: key, key: key });
          });
        } else {
          return header;
        }
      })
      .filter((header: KeyValue) => header.key && !header.key.includes(",")),
    ...headerArrayValues,
  ];

  const downloadFields = `&fields=${mergedDownloadHeaders
    .map((fields) => fields.key)
    .join()},CERT,RISDATE`;

  const apiData: apiDownloadProps[] = data[0]
    ? isPeerGroup
      ? [
          {
            appState,
            fileName: `${fileNameFormat("PeerGroup_L2")}.csv`,
            apiPath: "financials",
            text: "Download Results Data - raw",
            data: data.map((val: KeyValue, int: number) => {
              return {
                REPDATE: appState.filters.reportPeriod
                  ? appState.filters.reportPeriod[int]
                  : "",
                PEER_GROUP: appState.ui.peerGroups
                  ? appState.ui.peerGroups[int]
                  : "",
                ...val,
              };
            }),
          },
          {
            appState,
            fileName: `${fileNameFormat(
              `${
                standardComparisonOptions.find(
                  (comparison: KeyValue) =>
                    appState.ui.comparisonType === comparison.value,
                )?.label
              } Peer Groups Step Two`,
            )}.csv`,
            apiPath: "financials",
            text: "Download Results Data - formatted",
            data: appState.ui.reportDownload,
          },
        ]
      : [
          {
            appState,
            fileName: `${fileNameFormat("Financials_L2")}.csv`,
            apiPath: "financials",
            text: "Download Results Data - raw",
            headers: [
              { label: "RISDATE", key: "RISDATE" },
              {
                label: isPeerGroup ? "PEER_GROUP" : "CERT",
                key: isPeerGroup ? "PEER_GROUP" : "CERT",
              },
              ...(sortObjValue(
                mergedDownloadHeaders,
                "key",
              ) as DownloadHeaders[]),
            ],
            downloadUrl: `${ApiBaseUrl()}/financials?filters=RISDATE:(${data
              .map((req: KeyValue) => req.RISDATE)
              .join(" OR ")}) AND CERT:${
              data[0].CERT
            }&sort_by=RISDATE&sort_order=DESC${downloadFields}`,
          },
          {
            appState,
            fileName: `${fileNameFormat(`${data[0].CERT} Financials L2`)}.csv`,
            apiPath: "financials",
            text: "Download Results Data - formatted",
            data: appState.ui.reportDownload,
          },
        ]
    : [];

  const selectableDetails = (index: number) => {
    const hasChangePending = columnsWithPendingChanges?.includes(index);
    const repDate = hasChangePending
      ? pendingFRDetails?.reportPeriod[index]
      : appState.filters.reportPeriod[index]
        ? appState.filters.reportPeriod[index]
        : appState.ui.latestReportPeriod;

    const buttonText = (
      <>
        {formatRepDate(repDate)}
        {hasChangePending
          ? ` - ${pendingFRDetails?.incomeBasis[index]} - ${pendingFRDetails?.unitType[index]}`
          : (!appState.ui.incomeBasis[index] && !appState.ui.unitType[index]) ||
              !standardReportTypes.includes(FRDetails.reportType)
            ? ""
            : FRDetails.unitType?.[index] && FRDetails.incomeBasis?.[index]
              ? ` - ${FRDetails.incomeBasis[index]} - ${FRDetails.unitType[index]}`
              : ` - ${appState.ui.incomeBasis[index]} - ${appState.ui.unitType[index]}`}
      </>
    );
    return (
      <div key={index}>
        <Typography
          sx={{
            color: "#fff",
            fontWeight: "bold",
            padding: 0,
          }}
          align="center"
          variant="body2"
        >
          Selectable Details
        </Typography>
        <Button
          id={`selectable-details-${index}`}
          key={`selectable-details-${index}`}
          disabled={
            !FRDetails.reportType ||
            FRDetails.reportType === "custom" ||
            dataLoading
          }
          sx={{
            "&.Mui-disabled": {
              backgroundColor: "#e0e0e0",
            },
            "&.MuiButton-root.MuiButton-containedSecondary": {
              margin: "8px 0",
              width: "100%",
              height: 40,
              fontSize: "1rem",
              fontWeight: "bold",
              backgroundColor: hasChangePending ? "#ece6d2" : "secondary",
              borderColor: hasChangePending ? "#ece6d2" : "secondary",
            },
            "& .MuiButton-endIcon": {
              "position": "absolute",
              "right": "1rem",
              "& svg": {
                fontSize: "1.5rem",
              },
            },
          }}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            handleOpen(e, index)
          }
          variant="contained"
          disableElevation
          color="secondary"
          endIcon={<MoreVertIcon />}
        >
          {buttonText}
        </Button>
        {selectableDetailsModal(
          newReportDisplay ? index + 1 : index,
          hasChangePending,
        )}
      </div>
    );
  };

  const selectableDetailsModal = (index: number, hasChangePending = false) => {
    const reportMonth = reportDate[currentIndex]
      ? reportDate[currentIndex].month
      : !isPeerGroup && appState.ui.latestReportPeriod.substring(4, 6);
    const reportDay = reportDate[currentIndex]
      ? reportDate[currentIndex].day
      : !isPeerGroup && appState.ui.latestReportPeriod.substring(6, 8);
    const reportYear = reportDate[currentIndex]
      ? reportDate[currentIndex].year
      : !isPeerGroup && appState.ui.latestReportPeriod.substring(0, 4);

    return (
      reportMonth &&
      reportDay &&
      reportYear && (
        <Popover
          transitionDuration={0}
          anchorEl={anchorEl}
          open={displayDetails[index] ? displayDetails[index] : false}
          anchorOrigin={{
            vertical: "center",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "center",
          }}
        >
          <ClickAwayListener
            onClickAway={() => {
              setNewReportDisplay(false);
              setDisplayDetails(displayDetails.map(() => false));
            }}
          >
            <Box
              id="selectable-details-display"
              sx={{
                "& .MuiPaper-root": {
                  padding: "1rem",
                  minWidth: { xs: 370, sm: 600 },
                },
                "& *": {
                  // removes dotted focus created by FDIC Header
                  outline: "0 !important",
                },
              }}
            >
              <Paper>
                <Grid container spacing={2}>
                  <Grid
                    size={
                      !standardReportTypes.includes(FRDetails.reportType)
                        ? 12
                        : 5
                    }
                  >
                    <Typography
                      sx={(theme) => ({
                        textAlign: "center",
                        color: theme.palette.primary.main,
                        padding: 0,
                        marginBottom: "1rem",
                        borderBottom: `1px solid ${theme.palette.secondary.main}`,
                      })}
                      variant="body2"
                    >
                      Report Date
                    </Typography>
                    <ReportingPeriod
                      inverse
                      index={currentIndex}
                      currentQuarterValue={`${reportMonth}${reportDay}`}
                      currentYearValue={reportYear}
                      setForm={
                        newReportDisplay
                          ? (val: any) => {
                              setHasModalChange(true);
                              setNewComparison(val);
                            }
                          : (val: any) => {
                              setHasModalChange(true);
                              setFRDetails(val);
                            }
                      }
                      form={newReportDisplay ? newComparison : FRDetails}
                    />
                  </Grid>
                  {FRDetails.reportType &&
                    standardReportTypes.includes(FRDetails.reportType) && (
                      <>
                        <Grid size={4}>{incomeBasisSelect(false)}</Grid>
                        <Grid size={3}>{unitTypeSelect(false)}</Grid>
                      </>
                    )}
                </Grid>
                <Box
                  sx={{
                    "marginTop": "1rem",
                    "width": "100%",
                    "textAlign": "center",
                    "& button.MuiButton-root.MuiButton-containedSecondary": {
                      marginTop: 14,
                      marginBottom: 0,
                      border: "1px solid rgba(26, 68, 128, 0.5)",
                      width: 165,
                      height: 32,
                      fontSize: 13,
                    },
                  }}
                >
                  {appState.filters.reportPeriod.length > 1 &&
                    !newReportDisplay &&
                    !isPeerGroup && (
                      <Button
                        id="selectable-details-remove"
                        onClick={() => removeAndClose()}
                        variant="contained"
                        disableElevation
                        color="secondary"
                        style={{ margin: "0px 10px" }}
                      >
                        Remove Column
                      </Button>
                    )}
                  <Button
                    id="selectable-details-done"
                    onClick={newReportDisplay ? generateReport : handleClose}
                    variant="contained"
                    disableElevation
                    color="secondary"
                    style={{ margin: "0px 10px" }}
                  >
                    {newReportDisplay
                      ? "Update Report"
                      : hasModalChange
                        ? "Continue"
                        : "Close"}
                  </Button>
                </Box>
              </Paper>
            </Box>
          </ClickAwayListener>
        </Popover>
      )
    );
  };

  const updateReportStructure = (data: KeyValue[] | null) => {
    if (data?.length && data[0]?.fields.length) {
      data[0].structureID = "customResults";
      setFRDetails({
        ...FRDetails,
        reportType: "customResults",
      });
      updateUi("reportStructure", data);
      updateUi("reportType", "customResults");
      updateUi("loadModal", false);
    }
  };

  const ignoredVariables = React.useMemo(
    () => ["NAMEFULL", "TOTAL", "", " ", "  ", "undefined"],
    [],
  );

  const loadReportCustomVariables = React.useMemo(() => {
    const keys = [
      "variable_ytd",
      "variable_ytdr",
      "variable_qtly",
      "variable_qtlyr",
    ];

    return (appState.ui?.reportStructure || [])
      .flatMap((section: KeyValue) => section?.fields || [])
      .map((field: any) => {
        const foundKey = keys.find((k) => field[k]);
        return foundKey ? field[foundKey] : null;
      })
      .filter(
        (val: any): val is string =>
          !!val && !ignoredVariables.includes(val.trim()),
      );
  }, [appState.ui?.reportStructure, ignoredVariables]);

  const reportTypeLabel =
    customReportHeaderDisplay || FRDetails.reportType === "customResults"
      ? "Customize a Report"
      : !defaultReportTypes &&
          FRDetails.reportType &&
          reportTypes.filter(
            (report: KeyValue) => report.id === FRDetails.reportType,
          )[0]
        ? reportTypes.filter(
            (report: KeyValue) => report.id === FRDetails.reportType,
          )[0].title
        : Array.isArray(appState?.ui?.reportStructure) &&
            appState.ui.reportStructure.length > 0
          ? appState.ui.reportStructure[0]?.label
          : "";
  const incomeLabel = customReportHeaderDisplay
    ? "Filter Income Basis By:"
    : "Income Basis";
  const unitTypeLabel = customReportHeaderDisplay
    ? "Filter Unit Type By:"
    : "Unit Type";

  const incomeBasisSelect = (customReport?: boolean) => (
    <>
      <Typography
        sx={(theme) =>
          customReport
            ? {
                color: "#fff",
                fontWeight: "bold",
                padding: 0,
              }
            : {
                textAlign: "center",
                color: theme.palette.primary.main,
                padding: 0,
                marginBottom: "1rem",
                borderBottom: `1px solid ${theme.palette.secondary.main}`,
              }
        }
        variant="body2"
      >
        {incomeLabel}
      </Typography>
      {!customReport && (
        <Typography
          variant="h2"
          align="center"
          id="selected-income-basis-value"
          style={{ lineHeight: 1, paddingBottom: ".5rem" }}
        >
          {currentIncomeBasis}
        </Typography>
      )}
      {(reportMetaData.income_basis.length > 1 &&
        FRDetails.reportType !== "") ||
      customReport ? (
        <FormControl variant="outlined" fullWidth size="small" margin="dense">
          <Select
            native
            labelId={`select-income-basis-label`}
            id={`select-income-basis`}
            value={currentIncomeBasis}
            onChange={(e: any) => {
              handleIncomeBasisChange(e);
            }}
          >
            {incomeBasisChoice.map((option) => (
              <option key={option.value} value={[option.value]}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormControl>
      ) : (
        nonEditable
      )}
    </>
  );

  const unitTypeSelect = (customReport?: boolean) => (
    <>
      <Typography
        sx={(theme) =>
          customReport
            ? {
                color: "#fff",
                fontWeight: "bold",
                padding: 0,
              }
            : {
                textAlign: "center",
                color: theme.palette.primary.main,
                padding: 0,
                marginBottom: "1rem",
                borderBottom: `1px solid ${theme.palette.secondary.main}`,
              }
        }
        variant="body2"
      >
        {unitTypeLabel}
      </Typography>
      {customReport ? (
        <FormControl variant="outlined" fullWidth size="small" margin="dense">
          <Select
            native
            labelId={`select-unit-type-label`}
            id={`select-unit-type`}
            value={currentUnitType}
            onChange={(e: any) => {
              handleUnitChange(e);
            }}
          >
            {unitTypeChoice.map((option) => {
              return (
                <option key={option.value} value={[option.value]}>
                  {option.label}
                </option>
              );
            })}
          </Select>
        </FormControl>
      ) : (
        <>
          <Typography
            id="unit-type-toggle"
            variant="h2"
            align="center"
            sx={{ lineHeight: 1, paddingBottom: ".5rem" }}
          >
            {currentUnitType}
          </Typography>
          {reportMetaData.unit_type.length > 1 &&
          FRDetails.reportType !== "" ? (
            <Typography component="div">
              <Grid
                component="label"
                container
                spacing={1}
                sx={{
                  marginTop: "6px",
                  marginLeft: "24px",
                  alignItems: "center",
                }}
              >
                <Grid
                  sx={(theme) => ({
                    color: customReport
                      ? theme.palette.secondary.main
                      : theme.palette.primary.dark,
                    fontWeight: "bold",
                  })}
                >
                  $
                </Grid>
                <Grid>
                  <Switch
                    id="toggle-unit-type"
                    checked={currentUnitType === "%"}
                    color="default"
                    onChange={(e: any) => handleUnitChange(e)}
                    name={`unitType_${currentIndex}`}
                  />
                </Grid>
                <Grid
                  sx={(theme) => ({
                    color: customReport
                      ? theme.palette.secondary.main
                      : theme.palette.primary.dark,
                    fontWeight: "bold",
                  })}
                >
                  %
                </Grid>
              </Grid>
            </Typography>
          ) : (
            nonEditable
          )}
        </>
      )}
    </>
  );

  const FinancialReportDetailsForm = (
    <Box
      sx={{
        marginTop: customReportHeaderDisplay ? 5 : 0,
        bgcolor: "primary.main",
        padding: 1,
        width: "fit-content",
        minWidth: "100%",
      }}
    >
      <Grid container spacing={2} sx={{ flexWrap: "nowrap" }}>
        <Grid
          size={{ xs: 12, md: 8 }}
          sx={(theme) =>
            customReportHeaderDisplay
              ? {}
              : {
                  "minWidth": {
                    xs: 300,
                    sm: 350,
                    md: 477,
                  },
                  "left": 0,
                  "zIndex": 1,
                  "backgroundColor": theme.palette.primary.main,
                  "&.MuiGrid-root": {
                    paddingLeft: 1.8,
                    paddingRight: 4,
                  },
                }
          }
        >
          {!customVariant ? (
            <>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: "bold",
                  padding: 0,
                }}
                variant="body2"
              >
                Report Type
              </Typography>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "14px",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <FormControl variant="outlined" size="small" margin={"dense"}>
                  <Select
                    sx={{
                      "&.MuiSelect-outlined": {
                        borderRadius: 15,
                      },
                    }}
                    inputProps={{ "aria-label": "Select a Report Type" }}
                    native
                    labelId={`select-report-type-label`}
                    id={`select-report-type`}
                    value={fromLevelOne ? undefined : FRDetails.reportType}
                    onChange={(e: any) => handleReportChange(e)}
                  >
                    <option value="">Select a Report</option>
                    {reportTypes.map((value: KeyValue, index: number) => {
                      const int = (index + 1).toString();
                      const reportTypeValue = value.id
                        ? value.id
                        : value &&
                          value.title
                            .toLowerCase()
                            .replace(/,/g, "")
                            .replace(/ /g, "-");
                      return (
                        <option key={int} value={reportTypeValue}>
                          {value.title}
                        </option>
                      );
                    })}
                    {customReportResultsDisplay ? (
                      <option value="customResults">
                        Customize Results Display
                      </option>
                    ) : (
                      <option value="custom">Customize a Report</option>
                    )}
                    <option value="loadModal">Load a Shared Report Code</option>
                  </Select>
                </FormControl>
              </div>
            </>
          ) : (
            <Typography
              component="h2"
              sx={{
                fontSize: 32,
                color: "#ffffff",
                paddingLeft: "1.5rem",
              }}
            >
              Create a Custom Report
            </Typography>
          )}
        </Grid>
        {customReportHeaderDisplay ? (
          <>
            <Grid
              size={{
                md: 2,
                sm: 6,
              }}
            >
              {incomeBasisSelect(true)}
            </Grid>
            <Grid
              size={{
                md: 2,
                sm: 6,
              }}
            >
              {unitTypeSelect(true)}
            </Grid>
          </>
        ) : (
          <>
            {appState.ui.reportType === "demographics" &&
              institutionsData !== undefined && (
                <Grid
                  style={{ minWidth: 220 }}
                  size={{
                    xs: 12,
                    md: 4,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{
                      color: "white",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    Current Data as of:
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: "white",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    {institutionsData.RUNDATE}
                  </Typography>
                </Grid>
              )}
            {Array.isArray(appState.filters.reportPeriod) ? (
              appState.filters.reportPeriod.map(
                (report: string, index: number) => {
                  return (
                    <Grid
                      key={`${report}_${index}`}
                      sx={{
                        paddingLeft: 1.5,
                        minWidth: 220,
                      }}
                      size={{
                        xs: 12,
                        md: 4,
                      }}
                    >
                      {selectableDetails(index)}
                    </Grid>
                  );
                },
              )
            ) : (
              <></>
            )}
          </>
        )}
      </Grid>
      {appState.ui.reportStructure && hideSearchButton && !loadCustomReport ? (
        <Typography
          id="report-structure-label"
          style={{
            fontWeight: "bold",
            color: "#ffffff",
            width: "fit-content",
            fontSize: 14,
            position: "sticky",
            left: 8,
            zIndex: 1,
          }}
        >
          {appState.ui.reportType === "demographics"
            ? reportTypeLabel
            : `${reportTypeLabel} - Dollar Figures in Thousands (USD) or Percent of Average Assets Annualized`}
        </Typography>
      ) : undefined}
    </Box>
  );

  return (
    <>
      {!hideSearchButton && !isPeerGroup && !pre1992 ? (
        appState.ui.limitInactiveDate ? (
          <Typography
            variant="body2"
            id="inactive_intro"
            sx={{
              marginTop: 5,
              color: (theme) => theme.palette.primary.main,
            }}
          >
            This institution is closed, but you can still research and generate
            financial reports up until its closing.
          </Typography>
        ) : appState.ui.noFRdata ? (
          <Typography
            variant="body2"
            sx={{
              marginTop: "1rem",
              color: (theme) => theme.palette.primary.main,
            }}
          >
            The selected institution has not yet filed a Call Report. Check back
            later for financial reports.
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              paddingTop: "2.5rem",
              color: (theme) => theme.palette.primary.main,
            }}
          >
            Research an institution by generating financial reports and trends
            over time. Choose from the list below of pre-made reports that
            target the data you wish to display.
          </Typography>
        )
      ) : undefined}
      {pre1992 ? (
        <Alert severity="warning" style={{ marginTop: "1rem" }}>
          This institution closed prior to the keeping of this kind of financial
          data. Reporting is unavailable.
        </Alert>
      ) : (
        <>
          <Box
            sx={{
              ...(isPeerGroup &&
                customReportResultsDisplay && {
                  "height": 42,
                  "#dataDownload": {
                    height: 40,
                    display: "flex",
                    position: "relative",
                    marginLeft: "auto",
                    right: 182,
                    bottom: 10,
                  },
                }),
              ...(isPeerGroup &&
                !customReportResultsDisplay && {
                  "height": 42,
                  "marginTop": -6,
                  "marginBottom": 8,
                  "#dataDownload": {
                    height: 40,
                    marginTop: 0,
                    display: "flex",
                    position: "relative",
                    marginRight: 0,
                    marginLeft: "auto",
                    bottom: -50,
                    right: 0,
                  },
                }),
              ...(!isPeerGroup &&
                customReportResultsDisplay && {
                  "#dataDownload": {
                    height: 40,
                    display: "flex",
                    position: "relative",
                    marginLeft: "auto",
                    right: 369,
                    top: 45,
                  },
                }),
              ...(!isPeerGroup &&
                !customReportResultsDisplay && {
                  "#dataDownload": {
                    height: 40,
                    display: "flex",
                    position: "relative",
                    marginLeft: "auto",
                    top: 45,
                    right: 186,
                  },
                }),
            }}
          >
            {hideSearchButton && !customReportHeaderDisplay && (
              <>
                <ApiDataDownload
                  app={currentApp}
                  apiData={apiData}
                  appState={appState}
                />
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "right",
                    gap: 2,
                    mt: 5,
                    mb: -5,
                  }}
                >
                  {customReportResultsDisplay && (
                    <Button
                      onClick={() => {
                        FRDetails.reportType === "customResults" &&
                          updateUi(
                            "customVariables",
                            loadReportCustomVariables,
                          );
                        setFetchReportData(true);
                        setFRDetails({
                          ...FRDetails,
                          reportType: "custom",
                          ...(FRDetails.reportType === "customResults" && {
                            customVariables: loadReportCustomVariables,
                          }),
                        });
                        updateUi("reportType", "custom");
                      }}
                      id="edit-variables-button"
                      variant="contained"
                      color="secondary"
                      sx={{
                        height: 40,
                        ...(isPeerGroup
                          ? {
                              position: "relative",
                              bottom: 90,
                            }
                          : {
                              bottom: 35,
                            }),
                      }}
                    >
                      Edit Variables Selected
                    </Button>
                  )}
                  {!isPeerGroup && (
                    <Button
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        handleOpen(
                          e,
                          appState.filters.reportPeriod.length,
                          true,
                        )
                      }
                      id="new-comparison-report"
                      variant="contained"
                      color="secondary"
                      sx={{
                        bottom: 35,
                        height: 40,
                      }}
                    >
                      <AddIcon /> Comparison Period
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>

          {appState.ui.noFRdata &&
          window.location.pathname
            .toLowerCase()
            .includes("/financialreporting/details/") ? (
            <></>
          ) : (
            <div style={{ overflowX: "scroll", overflowY: "hidden" }}>
              {!customReportHeaderDisplay && formHeader}
              {FinancialReportDetailsForm}
              {(hasPendingUpdate || !hideSearchButton) && !customVariant ? (
                <Box
                  style={{
                    position: "sticky",
                    left: 0,
                  }}
                  sx={{
                    width: "100%",
                    textAlign: "center",
                    paddingTop: updateReportButton ? 0 : 2,
                    bgcolor: updateReportButton ? "primary.main" : undefined,
                  }}
                >
                  <Button
                    id="report-search"
                    disabled={!FRDetails.reportType}
                    variant="contained"
                    color="secondary"
                    style={{ width: 165 }}
                    onClick={generateReport}
                  >
                    {customReportButton
                      ? "Customize Report"
                      : (hasPendingUpdate || updateReportButton) &&
                          FRDetails.reportType === appState.ui.reportType
                        ? "Update Report"
                        : "Generate Report"}
                  </Button>
                </Box>
              ) : undefined}
              {customReportHeaderDisplay ? (
                <FinancialCustomReportStructure
                  data={data}
                  customVariant={customVariant}
                  returnPath={returnPath}
                />
              ) : dataLoading ? (
                <LoadingIndicator
                  text="Loading"
                  spinnerSize={50}
                  textSx={{
                    textAlign: "center",
                    marginTop: ".5rem",
                    fontSize: "1.5rem",
                    position: "sticky",
                    left: 0,
                  }}
                  spinnerSx={{
                    textAlign: "center",
                    marginTop: ".5rem",
                    fontSize: "1.5rem",
                    position: "sticky",
                    left: 0,
                  }}
                />
              ) : appState.ui?.loadModal ? (
                <div>
                  <LoadReportModal
                    showLoadReportModal={appState?.ui?.loadModal}
                    currentApp={currentApp}
                    onClose={handleModalClose}
                    onSubmit={updateReportStructure}
                  />
                </div>
              ) : (
                <FinancialReportStructure
                  data={institutionsData ? [institutionsData, ...data] : data}
                  noInstName={isPeerGroup}
                  peerGroupsInfo={peerGroupsInfo}
                />
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
